import React, { useState } from "react";
import { useFormContext, useController } from "react-hook-form";
import { FormAutocompleteProps } from "./FormAutocomplete.types";
import { cn } from "@/utils/cn";
import { Input } from "@/components/atoms/Input";

export const FormAutocomplete: React.FC<FormAutocompleteProps> = ({
    name,
    control,
    label,
    placeholder = "Escribe para buscar...",
    className,
    options,
    disabled,
    resetFields,
    onChange,
    renderOption,
}) => {
    const { setValue } = useFormContext();
    const {
        field,
        fieldState: { error },
    } = useController({ name, control });

    const [inputValue, setInputValue] = useState(
        options.find(op => op.value === field.value)?.label ?? ""
    );
    const [isOpen, setIsOpen] = useState(false);

    const filteredOptions = options.filter((option) =>
        option.label.toLowerCase().includes(inputValue.toLowerCase())
    );

    const handleSelect = (option: { label: string; value: string }) => {
        setInputValue(option.label);
        setIsOpen(false);
        field.onChange(option.value);
        onChange?.(option.value);
        resetFields?.forEach((fieldName) => setValue(fieldName, ""));
    };

    return (
        <div className="relative w-full">
            {label && <label className="mb-1 text-sm font-medium">{label}</label>}
            <Input
                value={inputValue}
                placeholder={placeholder}
                onChange={(e) => {
                    setInputValue(e.target.value)
                    setIsOpen(true);
                }}
                onFocus={() => setIsOpen(true)}
                onBlur={() => setTimeout(() => setIsOpen(false), 100)}
                onClear={() => {
                    setInputValue("");
                    onChange && onChange("");
                }}
                className={cn(
                    "w-full border rounded px-3 py-2",
                    error && "border-red-500 focus:ring-red-500",
                    className
                )}
                disabled={disabled}
            />
            {isOpen && (
                <ul className="absolute z-10 bg-white dark:bg-[--secondary-800] border rounded mt-1 w-full max-h-60 overflow-auto">
                    {filteredOptions.length === 0 ? (
                        <li className="px-4 py-6 text-center text-sm text-muted-foreground">
                            Sin datos
                        </li>
                    ) : (
                        filteredOptions.map((option) => (
                            <li
                                key={option.value}
                                onMouseDown={() => handleSelect(option)}
                                className="px-4 py-2 cursor-pointer hover:bg-gray-100 dark:hover:bg-[--secondary-700]"
                            >
                                {!!renderOption ? renderOption(option) : option.label}
                            </li>
                        ))
                    )}
                </ul>
            )}
            {error && (
                <p className="text-red-600 text-sm mt-1" role="alert">
                    {error.message}
                </p>
            )}
        </div>
    );
};
